'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import BrandMark from './BrandMark'
import { t, citiesPath } from '../lib/i18n'

/**
 * Site header.
 *
 * REBUILT on the pattern from the True Enterprises site-a-swede build, which is the best header
 * in the estate. The previous one had a real box-model bug, visible on every page:
 *
 *   "Ciudades"  had  borderBottom: '2px solid …'  AND  paddingBottom: '0.5rem'
 *   "Contacto"  had  neither
 *
 * Both sat in a `display:flex; align-items:center` row. Flex centres each item's BORDER-BOX, so
 * Ciudades' box was ~10px taller at the bottom and the two text baselines landed ~5px apart.
 * That is the "text on the right side is not even aligned": a box-model bug, not a spacing one.
 * It also flipped fontWeight 500 -> 600 on the active link, which reflows the link's width.
 *
 * The four rules that make a nav align, taken from the TE build:
 *
 *   1. The bar has a FIXED height (--header-h). Its height is not derived from its children, so
 *      no child can move the vertical centre.
 *   2. EVERY link has identical, symmetric padding-block — top and bottom equal, on all of them.
 *   3. The active underline is an absolutely-positioned ::after. It takes ZERO layout space, so
 *      active and inactive links are byte-for-byte the same size.
 *   4. The active state changes COLOUR ONLY. Never font-weight, never a layout property.
 *
 * THE CSS IS NO LONGER OURS, and that is the point. It now lives in Composer's design system as the
 * `sticky` Header variant (byzantium/composer, packages/sections/src/chrome.css) and arrives here
 * inside app/tokens.generated.css. This header previously existed only as hand-written CSS in
 * individual client repos, which is exactly why every new site rebuilt it and the alignment bug
 * kept recurring. Do NOT add header CSS to app/skin.css — change chrome.css in Composer and run
 * `npm run skin:sync`.
 *
 * The class names are Composer's (`cx-*`). The `data-cx-nav*` hooks its framework-free chrome.js
 * binds to are inert here: this component keeps its own React state, because it has usePathname for
 * the active link and can close the drawer on navigation, which a static site cannot.
 */

/**
 * The same page, in the other language. A language toggle that dumps you on the home page has
 * thrown away what you were looking at, which is the one thing you cared about.
 */
function alternatePathForCurrent(pathname, locale) {
  if (!pathname) return locale === 'es' ? '/en' : '/'
  if (locale === 'es') {
    if (pathname === '/') return '/en'
    if (pathname.startsWith('/parques-caninos')) {
      return '/en/dog-parks' + pathname.slice('/parques-caninos'.length)
    }
    return '/en'
  }
  if (pathname === '/en') return '/'
  if (pathname.startsWith('/en/dog-parks')) {
    return '/parques-caninos' + pathname.slice('/en/dog-parks'.length)
  }
  return '/'
}

export default function Header() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)

  // Derived, not passed. Header is rendered by the ROOT layout — above any per-language layout that
  // could hand it a prop — and reading headers() up there would opt all 672 static pages into
  // dynamic rendering. usePathname costs nothing and is always right.
  const locale = pathname === '/en' || pathname?.startsWith('/en/') ? 'en' : 'es'
  const T = t(locale)

  // The "English" link used to point at /dog-parks-in-spain — a single static page with a hardcoded
  // city list and no map. It looked like a language toggle and behaved like a dead end. It is now a
  // real toggle: it swaps the language of THE PAGE YOU ARE ON, which is the only thing a language
  // control should ever do.
  const other = alternatePathForCurrent(pathname, locale)

  const LINKS = [{ href: citiesPath(locale), label: T.cities }]

  useEffect(() => setOpen(false), [pathname]) // close the drawer on navigation
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const isActive = (href) => pathname === href || pathname?.startsWith(href + '/')

  return (
    <header className="cx-header">
      <div className="cx-header__inner">
        <Link className="cx-brand" href="/" aria-label="DogParks, inicio">
          <BrandMark size={28} />
          <span className="cx-brand__word">DogParks</span>
        </Link>

        <button
          className="cx-navToggle"
          aria-label="Menú"
          aria-controls="cx-primary-nav"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>

        <nav className="cx-nav" id="cx-primary-nav" aria-label="Principal" data-open={open}>
          <ul className="cx-nav__links">
            {LINKS.map((l) => (
              <li key={l.href}>
                <Link href={l.href} aria-current={isActive(l.href) ? 'page' : undefined}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <Link href={other} className="cx-nav__lang" hrefLang={locale === 'es' ? 'en' : 'es'}>
            {T.otherLang}
          </Link>
          <a
            className="cx-nav__cta"
            href="mailto:info@dogparks.es?subject=Sugerencia%20o%20correcci%C3%B3n"
          >
            {T.contact}
          </a>
        </nav>
      </div>
    </header>
  )
}
